import { Edit } from "@mui/icons-material";
import { Big } from "big.js";
import {
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { DisbursementType, Stage } from "../../shared/CaseConfiguration";
import { OptionalBig } from "../../shared/CostModel";
import { EditableCurrencyTableCell } from "../EditableCurrencyTableCell";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { COLUMN_HEADING } from "../general/tableStyles";
import { getDisbursementsTotal } from "./BudgetUtils.ts";
import { CostInputDialog } from "./CostInputDialog";

/**
 * Holds editable cost parameters per stage relevant for the table component:
 * the inputs, and what settlements dropped of the stage, fixed, on top of
 * which the inputs come
 */
export type EditableCostsPerStage = {
  stage: Stage;
  lawyerCosts: number;
  disbursements: { [type in DisbursementType]: number };
  fixed?: {
    lawyerCosts: number;
    disbursements: { [type in DisbursementType]: number };
  };
};

export const EditableCostsPerStageTable = ({
  editable = true,
  costs,
  leftAt,
  updateCosts,
}: {
  costs: EditableCostsPerStage[];
  // the stage at which the party left the case, which fixed its costs
  leftAt?: Stage;
  updateCosts: (newCosts: typeof costs) => void;
  editable?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });

  const [disbursementsToEdit, setDisbursementsToEdit] = useState<
    | {
        title: string;
        stage: Stage;
        disbursements: { [type in DisbursementType]?: number };
        fixed?: { [type in DisbursementType]?: number };
        onSave: (disbursements: {
          [type in DisbursementType]?: number;
        }) => void;
      }
    | undefined
  >();

  function updateLawyerCostsOfStage(stage: Stage, lawyerCosts: number) {
    updateCosts(
      costs.map((costs) =>
        costs.stage === stage ? { ...costs, lawyerCosts } : costs,
      ),
    );
  }
  function updateDisbursementsOfStage(
    stage: Stage,
    disbursements: { [type in DisbursementType]?: number },
  ) {
    updateCosts(
      costs.map((costs) =>
        costs.stage === stage
          ? {
              ...costs,
              disbursements: { ...costs.disbursements, ...disbursements },
            }
          : costs,
      ),
    );
  }

  const totalLawyerCosts = new OptionalBig();
  const totalDisbursements = new OptionalBig();
  const budgetTotal = new OptionalBig();

  const figure = { minWidth: 130, ...TABULAR_NUMERALS } as const;
  const total = { ...figure, fontWeight: 700 } as const;

  return (
    <>
      <TableContainer sx={{ userSelect: "none" }}>
        <Table
          size="small"
          sx={{
            // the first and the last column line up with the card's own inset
            "& .MuiTableCell-root:first-of-type": { pl: 2.5 },
            "& .MuiTableCell-root:last-of-type": { pr: 2.5 },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              <TableCell sx={{ ...COLUMN_HEADING, minWidth: 154 }}>
                {t("budget.table.header.stage")}
              </TableCell>
              <TableCell align="right" sx={COLUMN_HEADING}>
                {t("budget.stage.header.totalLawyerCosts")}
              </TableCell>
              <TableCell align="right" sx={COLUMN_HEADING}>
                {t("budget.stage.header.disbursements")}
              </TableCell>
              <TableCell align="right" sx={COLUMN_HEADING}>
                {t("budget.stage.header.total")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.map(({ stage, lawyerCosts, disbursements, fixed }, index) => {
              const fixedDisbursements = fixed
                ? getDisbursementsTotal(fixed.disbursements)
                : undefined;
              const disbursementTotal = getDisbursementsTotal(
                disbursements,
              ).add(fixedDisbursements ?? 0);
              const lawyerTotal = new Big(lawyerCosts).add(
                fixed?.lawyerCosts ?? 0,
              );
              const stageTotal = disbursementTotal.add(lawyerTotal);
              totalLawyerCosts.addBig(lawyerTotal);
              totalDisbursements.addBig(disbursementTotal);
              budgetTotal.addBig(stageTotal);
              const fixedCaption = (amount: number | undefined) =>
                amount === undefined || amount <= 0
                  ? undefined
                  : leftAt === undefined
                    ? t("budget.table.fixed", { amount: currency.format(amount) })
                    : t("budget.table.leftTheCase", {
                        amount: currency.format(amount),
                        stage: t(`stages.${Stage[leftAt]}`),
                      });

              return (
                <TableRow key={"stageCosts_" + index} hover>
                  <TableCell sx={{ py: 1.25, fontWeight: 500 }}>
                    {t(`stages.${Stage[stage]}`)}
                  </TableCell>
                  <EditableCurrencyTableCell
                    editable={editable}
                    align="right"
                    currency={currency}
                    label={t("budget.stage.header.totalLawyerCosts")}
                    // shown, the stage's law firm fees; edited, what comes on
                    // top of what settlements dropped
                    value={editable ? lawyerCosts : lawyerTotal.toNumber()}
                    caption={fixedCaption(fixed?.lawyerCosts)}
                    onValueChange={(lawyerCosts) =>
                      updateLawyerCostsOfStage(stage, lawyerCosts)
                    }
                  />

                  <TableCell
                    align="right"
                    sx={{
                      ...figure,
                      cursor: editable ? "pointer" : undefined,
                    }}
                    onClick={() =>
                      editable &&
                      setDisbursementsToEdit({
                        title: t("budget.stage.header.disbursements"),
                        stage,
                        disbursements,
                        fixed: fixed?.disbursements,
                        onSave: (disbursements) =>
                          updateDisbursementsOfStage(stage, disbursements),
                      })
                    }
                  >
                    {fixedCaption(fixedDisbursements?.toNumber()) && (
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{ color: "text.secondary" }}
                      >
                        {fixedCaption(fixedDisbursements?.toNumber())}
                      </Typography>
                    )}
                    {currency.format(disbursementTotal.toNumber())}
                    {editable && (
                      <IconButton
                        aria-label={t("budget.stage.header.disbursements")}
                        size="small"
                        sx={{ ml: theme.spacing(1) }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ ...figure, fontWeight: 600 }}>
                    {currency.format(stageTotal.toNumber())}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow key={"total"} sx={{ bgcolor: "background.default" }}>
              <TableCell sx={{ py: 1.5, fontWeight: 700 }}>
                {t("budget.stage.header.total")}
              </TableCell>
              <TableCell align="right" sx={total}>
                {currency.format(totalLawyerCosts.toNumber() || 0)}
              </TableCell>
              <TableCell align="right" sx={total}>
                {currency.format(totalDisbursements.toNumber() || 0)}
              </TableCell>
              <TableCell align="right" sx={total}>
                {currency.format(budgetTotal.toNumber() || 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {editable && disbursementsToEdit && (
        <CostInputDialog
          open={disbursementsToEdit !== undefined}
          onClose={(updatedCosts) => {
            setDisbursementsToEdit(undefined);
            if (!updatedCosts) return;
            disbursementsToEdit?.onSave(updatedCosts);
          }}
          title={disbursementsToEdit.title}
          subTitle={t(
            `stages.${Stage[disbursementsToEdit.stage]}`,
            Stage[disbursementsToEdit.stage],
          )}
          costs={disbursementsToEdit.disbursements}
          fixed={disbursementsToEdit.fixed}
        />
      )}
    </>
  );
};
